import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5050/api/v1';

const apiClient: AxiosInstance = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Helper to determine if we are on a student portal route
const isStudentRoute = () => {
    return window.location.pathname.startsWith('/student') || 
           window.location.pathname === '/login' ||
           window.location.pathname.startsWith('/payment');
};

// ─── Request interceptor ────────────────────────────────────────────
// Automatically attach the Bearer token from localStorage to every request.
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const tokenKey = isStudentRoute() ? 'studentAccessToken' : 'adminAccessToken';
        const token = localStorage.getItem(tokenKey);
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error),
);

// ─── Response interceptor ───────────────────────────────────────────
// Redirect to sign-in on 401 (except for the login request itself).
apiClient.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error) => {
        const isAuthRequest = error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/student-login');
        if (error.response?.status === 401 && !isAuthRequest) {
            const isStudent = isStudentRoute();
            const tokenKey = isStudent ? 'studentAccessToken' : 'adminAccessToken';
            localStorage.removeItem(tokenKey);
            window.location.href = isStudent ? '/login' : '/admin/signin';
        }
        return Promise.reject(error);
    },
);

export default apiClient;
